import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, FileText, Sparkles, ArrowRight, ShoppingBag, Inbox, Compass, Globe,
  BarChart3, Lock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  reportsApi, billingApi,
  type Entitlement, type ReportListItem,
} from '../lib/reportsApi';
import { PageHeader, SectionCard, Button, Badge, EmptyState, StatCard } from '../components/ui/light';

export default function InfluencerDashboard() {
  const { user } = useAuth();
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([reportsApi.list(8), billingApi.me().catch(() => null)])
      .then(([list, billing]) => {
        setReports(list.data.reports);
        setEntitlement(billing?.data.entitlement ?? list.data.entitlement);
      })
      .finally(() => setLoading(false));
  }, []);

  const planLabel =
    entitlement?.plan === 'pro'   ? 'Pro' :
    entitlement?.plan === 'scale' ? 'Scale' :
    'Free';

  return (
    <div className="theme-light l-page">
      <div className="l-shell py-8">
        <PageHeader
          eyebrow="Welcome"
          title={user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Your dashboard'}
          subtitle="Generate creator reports, review insights, and unlock more monetization paths."
          actions={
            <Link to="/reports/new">
              <Button variant="accent" iconLeft={<Plus className="w-4 h-4" />}>New report</Button>
            </Link>
          }
        />

        {/* ── Plan + free quota strip ─────────────────────────── */}
        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Plan"
            value={planLabel}
            hint={entitlement?.premium_unlocked ? 'Premium unlocked' : 'Free tier'}
            icon={entitlement?.premium_unlocked ? <Sparkles className="w-4 h-4" /> : undefined}
          />
          <StatCard
            label="Free reports"
            value={`${entitlement?.free_used ?? 0} / ${entitlement?.free_lifetime_cap ?? 5}`}
            hint={`${entitlement?.free_remaining ?? 0} remaining`}
          />
          <StatCard
            label="Total reports"
            value={reports.length.toString()}
            hint="Lifetime"
            icon={<FileText className="w-4 h-4" />}
          />
        </div>

        {/* ── Recent reports ──────────────────────────────────── */}
        <div className="grid lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-8">
            <SectionCard
              title="Recent reports"
              actions={
                <Link to="/reports">
                  <Button variant="ghost" size="sm" iconRight={<ArrowRight className="w-3.5 h-3.5" />}>
                    View all
                  </Button>
                </Link>
              }
            >
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14" style={{ background: 'var(--ch2-surface-2)', borderRadius: 'var(--ch2-radius-sm)' }} />
                  ))}
                </div>
              ) : reports.length === 0 ? (
                <EmptyState
                  icon={<FileText className="w-6 h-6" />}
                  title="No reports yet"
                  description="Paste a social link to generate your first creator monetization report."
                  action={
                    <Link to="/reports/new">
                      <Button variant="accent">Generate first report</Button>
                    </Link>
                  }
                />
              ) : (
                <ul className="space-y-2">
                  {reports.slice(0, 6).map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/reports/${r.id}`}
                        className="flex items-center gap-4 p-3 transition-colors"
                        style={{
                          background: 'var(--ch2-surface)',
                          border: '1px solid var(--ch2-border-2)',
                          borderRadius: 'var(--ch2-radius-sm)',
                        }}
                      >
                        <div
                          className="w-10 h-10 flex items-center justify-center shrink-0"
                          style={{ background: 'var(--ch2-accent-tint)', color: 'var(--ch2-accent-2)', borderRadius: 999 }}
                        >
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium" style={{ color: 'var(--ch2-ink)' }}>
                            @{r.handle ?? '—'}
                          </div>
                          <div className="l-subtle text-xs mt-0.5">
                            {r.platform ?? '—'} · {new Date(r.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <Badge variant={r.variant === 'premium' ? 'accent' : 'neutral'}>
                          {r.variant}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          {/* ── Upgrade CTA ────────────────────────────────────── */}
          <div className="lg:col-span-4">
            {entitlement?.premium_unlocked ? (
              <SectionCard title="Premium" subtitle="You're on the full-power plan" variant="muted">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5" style={{ color: 'var(--ch2-accent-2)' }} />
                  <div>
                    <div className="font-medium" style={{ color: 'var(--ch2-ink)' }}>
                      {planLabel} plan active
                    </div>
                    {entitlement.current_period_end && (
                      <div className="l-subtle text-xs">
                        Renews {new Date(entitlement.current_period_end).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
                <Link to="/settings" className="block mt-4">
                  <Button variant="ghost" size="sm" className="w-full">Manage subscription</Button>
                </Link>
              </SectionCard>
            ) : (
              <div
                className="p-6 flex flex-col gap-3 h-full"
                style={{
                  background: 'var(--ch2-ink)',
                  color: '#ffffff',
                  borderRadius: 'var(--ch2-radius-lg)',
                  boxShadow: 'var(--ch2-shadow-md)',
                }}
              >
                <div className="inline-flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4" style={{ color: '#FFD400' }} />
                  <span className="text-sm font-medium" style={{ color: '#FFD400' }}>Premium</span>
                </div>
                <h3 className="font-display font-bold text-xl mb-1">
                  Unlock the full playbook
                </h3>
                <p className="text-sm opacity-80">
                  Product matches, vendor connections, brand-deal pipeline, and the full action plan.
                </p>
                <ul className="space-y-1.5 text-sm opacity-90 my-2">
                  <li className="flex gap-2"><Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Premium reports unlimited</li>
                  <li className="flex gap-2"><Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Product + vendor matches</li>
                  <li className="flex gap-2"><Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Brand-deal opportunities</li>
                </ul>
                <Link to="/upgrade" className="mt-2">
                  <button
                    className="w-full px-4 py-2.5 font-medium text-sm"
                    style={{ background: '#FFD400', color: 'var(--ch2-ink)', borderRadius: 'var(--ch2-radius-sm)' }}
                  >
                    See plans →
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* ── Quick actions / Storefront sub-area ─────────────── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction icon={<Plus className="w-4 h-4" />} label="Generate new report"  to="/reports/new" />
          <QuickAction icon={<BarChart3 className="w-4 h-4" />} label="View all reports" to="/reports" />
          <QuickAction icon={<Sparkles className="w-4 h-4" />} label="Upgrade plan"      to="/upgrade" />
          <QuickAction icon={<Compass className="w-4 h-4" />} label="Browse storefront"  to="/explore" />
        </div>

        <div className="mt-10">
          <div className="l-section-eyebrow mb-3">Storefront</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SubLink icon={<ShoppingBag />} label="My products" to="/products" />
            <SubLink icon={<Inbox       />} label="My requests" to="/requests" />
            <SubLink icon={<Compass     />} label="Explore"     to="/explore" />
            <SubLink icon={<Globe       />} label="Your site"   to="/your-site" />
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon, label, to }: { icon: React.ReactNode; label: string; to: string }) {
  return (
    <Link
      to={to}
      className="l-card p-5 flex items-center gap-3 transition-all hover:shadow-l-md"
      style={{ color: 'var(--ch2-ink)' }}
    >
      <div
        className="w-9 h-9 flex items-center justify-center shrink-0"
        style={{ background: 'var(--ch2-accent-tint)', color: 'var(--ch2-accent-2)', borderRadius: 'var(--ch2-radius-sm)' }}
      >
        {icon}
      </div>
      <span className="font-medium text-sm">{label}</span>
      <ArrowRight className="w-4 h-4 ml-auto" style={{ color: 'var(--ch2-ink-3)' }} />
    </Link>
  );
}

function SubLink({ icon, label, to }: { icon: React.ReactNode; label: string; to: string }) {
  return (
    <Link
      to={to}
      className="l-card-flat p-4 flex items-center gap-3"
      style={{ color: 'var(--ch2-ink)' }}
    >
      <div className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--ch2-ink-3)' }}>
        {icon}
      </div>
      <span className="text-sm">{label}</span>
    </Link>
  );
}
